import React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Container,
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  Button,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Description as DescriptionIcon,
  Event as EventIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { apiWithAuth } from '../services/api';
import { getEventById } from '../services/eventService';
import { getImageUrl } from '../utils/imageUtils';
import Spinner from '../components/common/Spinner';
import ErrorMessage from '../components/common/ErrorMessage';

const PaymentInfo = () => {
  const { applicationId } = useParams();
  const history = useHistory();

  // Fetch application details
  const { data: application, isLoading: appLoading, error: appError } = useQuery(
    ['application', applicationId],
    async () => {
      const token = localStorage.getItem('token');
      const response = await apiWithAuth(token).get(`/applications/`);
      const apps = response.data;
      const app = apps.find(a => a.application_id === parseInt(applicationId));
      if (!app) throw new Error('Application not found');
      return app;
    }
  );

  // Fetch event details
  const { data: event, isLoading: eventLoading } = useQuery(
    ['event', application?.event_id],
    () => getEventById(application?.event_id),
    { enabled: !!application?.event_id }
  );

  if (appLoading || eventLoading) return <Spinner />;
  if (appError) return <ErrorMessage message={appError.message} />;
  if (!application) return <ErrorMessage message="Application not found" />;

  const isPaymentPending = 
    (application.status?.toLowerCase() === 'accepted' || application.status?.toLowerCase() === 'approved') &&
    application.payment_status === 'pending';
  
  const isPaymentConfirmed = application.payment_status === 'confirmed';
  const paymentDeadline = application.payment_deadline ? new Date(application.payment_deadline) : null;
  const isOverdue = paymentDeadline && paymentDeadline < new Date() && !isPaymentConfirmed;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => history.goBack()}
        sx={{ mb: 3 }}
      >
        Back
      </Button>

      <Typography variant="h4" gutterBottom sx={{ mb: 1 }}>
        Payment Information
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Complete your payment to secure your spot at this event
      </Typography>

      {/* Event Card */}
      {event && (
        <Card sx={{ mb: 3 }}>
          <Box sx={{ position: 'relative', height: 200, overflow: 'hidden' }}>
            <img
              src={event.image_url ? getImageUrl(event.image_url) : 'https://via.placeholder.com/800x400?text=Event'}
              alt={event.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              {event.title}
            </Typography>
            <Stack spacing={1} sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {event.date ? format(new Date(event.date), 'EEEE, MMMM d, yyyy') : 'TBD'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon fontSize="small" color="action" />
                <Typography variant="body2">{event.location}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Payment Status Alert */}
      {isPaymentConfirmed && (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Payment Confirmed! ✅
          </Typography>
          <Typography variant="body2">
            Your payment has been confirmed. Your spot at this event is secure!
            {application.payment_confirmed_at && (
              <span> Confirmed on {format(new Date(application.payment_confirmed_at), 'MMMM d, yyyy')}</span>
            )}
          </Typography>
        </Alert>
      )}

      {isPaymentPending && !isOverdue && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Payment Required
          </Typography>
          <Typography variant="body2">
            Please complete payment to secure your spot. 
            {paymentDeadline && (
              <span> Payment is due by <strong>{format(paymentDeadline, 'MMMM d, yyyy')}</strong></span>
            )}
          </Typography>
        </Alert>
      )}

      {isOverdue && (
        <Alert severity="error" icon={<WarningIcon />} sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Payment Overdue
          </Typography>
          <Typography variant="body2">
            The payment deadline has passed. Please contact the event organizer immediately to check if your spot is still available.
          </Typography>
        </Alert>
      )}

      {/* Payment Details Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon color="primary" />
          Payment Details
        </Typography>
        <Divider sx={{ my: 2 }} />
        
        <List>
          <ListItem>
            <ListItemIcon>
              <MoneyIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Vendor Fee"
              secondary={event?.fee ? `$${parseFloat(event.fee).toFixed(2)}` : 'Free'}
            />
          </ListItem>
          {paymentDeadline && (
            <ListItem>
              <ListItemIcon>
                <ScheduleIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Payment Deadline"
                secondary={format(paymentDeadline, 'EEEE, MMMM d, yyyy')}
              />
            </ListItem>
          )}
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color={isPaymentConfirmed ? 'success' : 'disabled'} />
            </ListItemIcon>
            <ListItemText
              primary="Payment Status"
              secondary={
                <Chip
                  label={isPaymentConfirmed ? 'Confirmed' : isPaymentPending ? 'Pending' : application.payment_status}
                  color={isPaymentConfirmed ? 'success' : isPaymentPending ? 'warning' : 'default'}
                  size="small"
                />
              }
            />
          </ListItem>
        </List>
      </Paper>

      {/* Payment Instructions */}
      {event?.payment_instructions && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon color="primary" />
            Payment Instructions
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography
            variant="body1"
            sx={{
              whiteSpace: 'pre-wrap',
              lineHeight: 1.8,
            }}
          >
            {event.payment_instructions}
          </Typography>
        </Paper>
      )}

      {!event?.payment_instructions && isPaymentPending && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          <Typography variant="body2">
            Payment instructions have not been provided yet. Please contact the event organizer for payment details.
          </Typography>
        </Alert>
      )}

      {/* Additional Information */}
      {event && (
        <>
          {(event.cancellation_policy || event.refund_policy) && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Policies
              </Typography>
              <Divider sx={{ my: 2 }} />
              {event.cancellation_policy && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Cancellation Policy
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {event.cancellation_policy}
                  </Typography>
                </Box>
              )}
              {event.refund_policy && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Refund Policy
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {event.refund_policy}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => history.push(`/applications`)}
            >
              View All Applications
            </Button>
            {event?.event_id && (
              <Button
                variant="contained"
                onClick={() => history.push(`/event/${event.event_id}`)}
              >
                View Event Details
              </Button>
            )}
          </Box>
        </>
      )}
    </Container>
  );
};

export default PaymentInfo;


